import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { addRequestQueries } from '@/api/endpoints/add-requests'
import { createProfile, profileQueries } from '@/api/endpoints/profiles'
import { apiErrorMessage } from '@/api/errors'
import { Form } from '@/components/kit/form-fields'
import { Button } from '@/components/ui/button'
import {
  emptyProfileFormValues,
  profileFormValuesFromAddRequest,
} from '@/features/profiles/from-add-request'
import { ProfileDetailsFields } from '@/features/profiles/profile-details-fields'
import {
  profileCreateFormSchema,
  type ProfileFormValues,
} from '@/features/profiles/profile-form-schema'
import { SimilarProfiles } from '@/features/profiles/similar-profiles'
import { toProfileCreateInput } from '@/features/profiles/to-create-input'

export const Route = createFileRoute('/_authed/profiles/new')({
  validateSearch: z.object({ add_request: z.uuid().optional().catch(undefined) }),
  component: NewProfilePage,
})

const profilesRoute = getRouteApi('/_authed/profiles')

function NewProfilePage() {
  const { add_request: addRequestUuid } = Route.useSearch()
  const listSearch = profilesRoute.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileCreateFormSchema),
    defaultValues: emptyProfileFormValues,
  })

  const addRequest = useQuery({
    ...addRequestQueries.detail(addRequestUuid ?? ''),
    enabled: Boolean(addRequestUuid),
  })

  useEffect(() => {
    if (addRequest.data && !form.formState.isDirty) {
      form.reset(profileFormValuesFromAddRequest(addRequest.data))
    }
  }, [addRequest.data, form])

  const name = useWatch({ control: form.control, name: 'name' })

  const create = useMutation({
    mutationFn: (values: ProfileFormValues) => createProfile(toProfileCreateInput(values)),
    onSuccess: async (profile) => {
      void queryClient.invalidateQueries({ queryKey: profileQueries.all })
      toast.success('Profile created')
      if (profile.uuid) {
        queryClient.setQueryData(profileQueries.detail(profile.uuid).queryKey, profile)
        await navigate({
          to: '/profiles/$uuid',
          params: { uuid: profile.uuid },
          search: listSearch,
        })
        return
      }
      // Leave the form without a uuid to open, so a retry cannot create the profile a second time.
      await navigate({ to: '/profiles', search: listSearch })
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  })

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/profiles" search={listSearch}>
            <ArrowLeft />
            Profiles
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">New profile</h1>
      </div>

      {addRequest.isError && (
        <p role="alert" className="text-destructive">
          Could not load the add request: {apiErrorMessage(addRequest.error)}
        </p>
      )}

      {addRequest.data && (
        <p className="text-sm text-muted-foreground">
          From add request{addRequest.data.server ? ` · ${addRequest.data.server}` : ''}
        </p>
      )}

      <Form form={form} onSubmit={(values) => create.mutate(values)} className="grid gap-4">
        <ProfileDetailsFields />
        <SimilarProfiles name={name} />
        <div className="flex flex-wrap gap-2 pt-2">
          <Button type="submit" disabled={create.isPending}>
            Create profile
          </Button>
        </div>
      </Form>
    </div>
  )
}
